import useCompanyQueryData from 'hooks/useCompanyQueryData';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useUserData, { UserType } from 'hooks/useUserData';
import { Dispatch, Fragment, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import _, { debounce } from 'lodash';
import { Avatar, Box, Dialog, DialogContent, Divider, Skeleton, Typography } from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import Search from 'pages/knowledge/components/SearchComponent';
import HandleOperation from 'components/utils/HandleOperation';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';
import GroupLineIcon from 'remixicon-react/GroupLineIcon';
import { Virtuoso } from 'react-virtuoso';
import UserAvatar from 'components/common/userAvatar/UserAvatar';
import Dot from 'components/common/dot/Dot';
import { styled, useTheme } from '@mui/material/styles';

type AddPeopleToAddPostModalProps = {
  open: boolean;
  selectedUser: UserType | null;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedUser: Dispatch<SetStateAction<UserType | null>>;
};

type UsersSearchResponseType = {
  totalElements?: number;
  totalNumber?: number;
  totalPages?: number;
  userResponse: UserType[];
};

const AddPeopleToAddPostModal = ({
  open,
  selectedUser,
  setOpen,
  setSelectedUser,
}: AddPeopleToAddPostModalProps) => {
  const theme = useTheme();
  const { companyId } = useUserData();
  const { shouldDisplayCompanyOnUserCards } = useCompanyQueryData();
  const [searchKeyword, setSearchKeyword] = useState('');
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const {
    data: people,
    isLoading,
    isFetching,
  } = useSearchItemQuery<UsersSearchResponseType>(
    {
      searchType: 'users',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    true,
  );

  const closeModal = () => {
    setOpen(false);
  };

  const updateSearch = (value: string) => {
    setSearchKeyword(value.trim());
  };
  const debounceFn = useCallback(debounce(updateSearch, 300), []);

  const users = useMemo(
    () =>
      people?.userResponse?.filter((user) => {
        const isUserMatchingWithSearch =
          user.userNameToDisplay.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          user.username.toLowerCase().includes(searchKeyword.toLowerCase());
        return searchKeyword ? isUserMatchingWithSearch : true;
      }) || [],
    [people, searchKeyword],
  );

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      sx={{ '& .MuiPaper-root': { height: '90%' } }}
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title">
        <Title>Select Member</Title>
      </CustomDialogTitle>

      <DialogContent id="dialog-description" dividers sx={{ p: 0 }} ref={contentRef}>
        <StickySearchContainer>
          <Search
            value={searchKeyword}
            placeholder="Search for people"
            setValue={debounceFn}
            filled
            triggerOnEnter
          />
        </StickySearchContainer>
        <HandleOperation
          isLoading={isLoading || isFetching}
          data={users}
          noDataComponent={
            <NoDataContainer>
              {searchKeyword && (
                <>
                  <SearchLineIcon size={120} color={theme.palette.custom.steel} />
                  <Typography component="p" mt={1.5}>
                    No users found with <b>"{searchKeyword}"</b>
                  </Typography>
                </>
              )}
              {!searchKeyword && (
                <>
                  <GroupLineIcon size={120} color={theme.palette.custom.steel} />
                  <Typography component="p" mt={1.5}>
                    No users selected
                  </Typography>
                </>
              )}
              {!searchKeyword && (
                <>
                  <GroupLineIcon size={120} color={theme.palette.custom.steel} />
                  <Typography component="p" mt={1.5}>
                    No users found
                  </Typography>
                </>
              )}
            </NoDataContainer>
          }
          skelton={
            <UserHoverContainer>
              <UserContainer>
                <Box display="flex" alignItems="flex-start">
                  <Skeleton animation="wave" variant="circular" width={40} height={40}>
                    <Avatar />
                  </Skeleton>
                  <Box ml={2}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={200}
                      height={15}
                      sx={{ mb: 0.5 }}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={100}
                      height={10}
                      sx={{ mr: 1 }}
                    />
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <Skeleton animation="wave" variant="rounded" width={20} height={20} />
                </Box>
              </UserContainer>
              <Divider />
            </UserHoverContainer>
          }
        >
          {people?.userResponse && people?.userResponse?.length > 0 && (
            <Virtuoso
              useWindowScroll
              customScrollParent={contentRef.current}
              data={users}
              itemContent={(index, item) => (
                <Fragment key={item.id}>
                  <UserHoverContainer
                    title={item.username}
                    onClick={() => {
                      setSelectedUser(item);
                      closeModal();
                    }}
                    sx={
                      selectedUser?.id === item.id
                        ? { backgroundColor: theme.palette.custom.whiteSmoke, transition: '0.5s' }
                        : {}
                    }
                  >
                    <UserContainer>
                      <UserAvatar
                        title={item.userNameToDisplay?.trim() || item.username}
                        avatarName={item.userNameToDisplay?.trim() || item.username}
                        subTitle={
                          <>
                            {item.title}
                            {shouldDisplayCompanyOnUserCards &&
                              (item?.primaryCompanyName || item?.userPrimaryCompanyName) && (
                                <>
                                  <Dot />
                                  {item?.primaryCompanyName || item?.userPrimaryCompanyName}
                                </>
                              )}
                          </>
                        }
                        avatarSize={40}
                        userName={item.username}
                        target="_blank"
                        profileImageURL={item.profileImageURL}
                      />
                    </UserContainer>
                    {users.length - 1 !== index && <Divider />}
                  </UserHoverContainer>
                </Fragment>
              )}
            />
          )}
        </HandleOperation>
      </DialogContent>
    </Dialog>
  );
};

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

const StickySearchContainer = styled(Box, { label: 'StickySearchContainer' })(({ theme }) => ({
  position: 'sticky',
  top: '0',
  backgroundColor: theme.palette.custom.white,
  zIndex: 1,
  padding: '12px 24px 12px',
}));

const UserHoverContainer = styled(Box, { label: 'UserHoverContainer' })(({ theme }) => ({
  padding: '0 24px',
  '&:hover': {
    backgroundColor: theme.palette.custom.whiteSmoke,
    transition: '0.5s',
  },
}));

const UserContainer = styled(Box, { label: 'UserContainer' })(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
  cursor: 'pointer',
}));

const NoDataContainer = styled(Box, { label: 'NoDataContainer' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  margin: '40px 24px 0',
});

export default AddPeopleToAddPostModal;
